import { useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * ApiEnvironmentSwitcher component that allows switching between local and production API endpoints
 * Only appears when running on localhost
 */
export const ApiEnvironmentSwitcher = () => {
  // Check if we're in a local environment
  const isLocalEnvironment = window.location.href.includes("localhost");

  // Track the current API environment
  const [useLocalApi, setUseLocalApi] = useState(() => {
    const savedSetting = localStorage.getItem("useLocalApi");
    // Default to true if not set or if explicitly set to "true"
    return savedSetting === null ? true : savedSetting === "true";
  });

  // Update localStorage and dispatch an event when setting changes
  const handleToggle = (checked: boolean) => {
    setUseLocalApi(checked);
    localStorage.setItem("useLocalApi", checked.toString());

    // Show notification
    toast.success(
      `API switched to ${checked ? "local" : "production"} environment`,
      {
        description: checked
          ? "Now using localhost APIs"
          : "Now using production APIs",
      }
    );

    // Dispatch a custom event to notify the app that the API environment has changed
    window.dispatchEvent(
      new CustomEvent("apiEnvironmentChanged", {
        detail: { useLocalApi: checked },
      })
    );
  };

  // Don't render anything if not in local environment
  if (!isLocalEnvironment) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="fixed bottom-4 right-4 z-50 bg-card border border-border rounded-lg shadow-lg p-3">
        <div className="flex items-center space-x-2">
          <Switch
            id="api-switch"
            checked={useLocalApi}
            onCheckedChange={handleToggle}
          />
          <Label htmlFor="api-switch" className="text-sm cursor-pointer">
            API:
          </Label>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant={useLocalApi ? "outline" : "destructive"}>
                {useLocalApi ? "Local" : "Production"}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <p>Switch between local and production API endpoints</p>
              <div className="text-xs text-muted-foreground mt-2 space-y-1">
                <p className="font-medium">Current endpoints:</p>
                {useLocalApi ? (
                  <>
                    <p>API: localhost:3123</p>
                    <p>Service: localhost:5100</p>
                  </>
                ) : (
                  <>
                    <p>API: enterprise.api.wealthbee.io</p>
                    <p>Service: jupiter.wealthbee.io</p>
                  </>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
};
